import {AfterViewInit, Component, inject, OnDestroy, DestroyRef, ChangeDetectionStrategy} from "@angular/core"
import {ActivatedRoute, NavigationEnd, NavigationSkipped, Router} from "@angular/router"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {filter} from "rxjs/operators"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"

/**
 * Base component for pages that support URL-driven create dialogs.
 * Handles opening and closing dialogs based on route changes.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: "",
})
export abstract class CreateItemComponent<T = unknown> implements AfterViewInit, OnDestroy {
    protected readonly router = inject(Router)
    protected readonly route = inject(ActivatedRoute)
    protected readonly dialog = inject(MatDialog)
    protected readonly destroyRef = inject(DestroyRef)
    private currentDialogRef: MatDialogRef<T> | null = null

    ngAfterViewInit() {
        // Check initial route state
        this.checkAndHandleRoute()

        // Listen for navigation events to handle create dialog opening/closing
        // We need to handle both NavigationEnd and NavigationSkipped because when
        // two navigations happen simultaneously (e.g., due to CDK menu + routerLink
        // interaction with query params), the first navigation gets cancelled and
        // the second gets skipped, but the URL still changes. NavigationSkipped
        // fires when Angular skips a navigation because we're "already" at the URL.
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd | NavigationSkipped => event instanceof NavigationEnd || event instanceof NavigationSkipped),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.checkAndHandleRoute()
            })
    }

    private checkAndHandleRoute() {
        // Always read the current URL from the router to get the most up-to-date state
        const currentUrl = this.router.url
        const isCreateRoute = currentUrl.includes("/create")

        if (isCreateRoute) {
            // Only open dialog if not already open
            if (!this.currentDialogRef) {
                this.openCreateDialog()
            }
        } else {
            this.closeCreateDialog()
        }
    }

    ngOnDestroy() {
        this.closeCreateDialog()
    }

    /**
     * Navigate to the create route for this page
     */
    protected navigateToCreate() {
        this.router.navigate(["create"], {relativeTo: this.route, queryParamsHandling: "preserve"})
    }

    /**
     * Navigate back to the parent route (closing the create dialog)
     */
    protected navigateToParent() {
        this.router.navigate(["."], {relativeTo: this.route, queryParamsHandling: "preserve", replaceUrl: true})
    }

    /**
     * Open the appropriate create dialog for this page.
     * Must be implemented by subclasses.
     */
    protected abstract openCreateDialog(): void | Promise<void>

    /**
     * Close any open create dialog
     */
    protected closeCreateDialog() {
        if (this.currentDialogRef) {
            this.currentDialogRef.close()
            this.currentDialogRef = null
        }
    }

    /**
     * Helper method for subclasses to set the current dialog reference
     */
    protected setDialogRef(dialogRef: MatDialogRef<T>) {
        this.currentDialogRef = dialogRef

        // Auto-navigate back when dialog is closed
        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                this.currentDialogRef = null

                // Handle the creation result and check if custom navigation was performed
                const shouldNavigateBack = this.handleDialogResult(result)

                // Only navigate back if no custom navigation was done and we're still on create route
                if (shouldNavigateBack !== false) {
                    const currentPath = this.route.firstChild?.snapshot.url[0]?.path
                    if (currentPath === "create") {
                        this.navigateToParent()
                    }
                }
            })
    }

    /**
     * Handle the dialog result when it's closed.
     * Subclasses can override this to implement custom navigation logic.
     * @param result The result returned from the dialog
     * @returns false if custom navigation was performed and default navigation should be skipped
     */
    protected handleDialogResult(_result: T): boolean | void {
        // Default implementation does nothing
    }
}
